import { useState } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { authClient } from '../../lib/auth-client';
import { SettingsLayout } from './SettingsLayout';
import { Button } from '../../components/ui/button';
import { PulsingDot } from '../../components/ui/pulsing-dot';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { GradientBorder } from '../../components/ui/gradient-border';
import { API_URL } from '../../lib/api';
import { getBarColor, getPlanBadgeStyle } from '../../lib/usage';
import { getDisplayPaidPlans } from '../../lib/plan-marketing';
import {
  CreditUsageGrid,
  PlanComparison,
  PlanIntro,
} from '../../components/PlanComparison';
import type { PlanInfo } from '../../hooks/useSubscription';

function saveCheckoutMeta(meta: Record<string, unknown>) {
  try {
    localStorage.setItem('checkout_previous_plan', JSON.stringify(meta));
  } catch {
    // localStorage unavailable (private browsing, quota exceeded)
    // Continue with checkout — success page will use generic fallback
  }
}

async function getErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    return data.error || fallback;
  } catch {
    return `Server error (${res.status})`;
  }
}


export function SettingsPlansPage() {
  const {
    subscription,
    planType,
    personalWorkspaceEnabled,
    plans,
    topUps,
    isLoading: subLoading,
    refetch: refetchSubscription,
  } = useSubscription();
  const {
    isLoading: balanceLoading,
    periodEnd,
    usagePercent,
    topUpCredits,
    monthlyRemaining,
    periodAllowance,
  } = useCreditBalance();
  const [confirmAction, setConfirmAction] = useState<{
    type: 'change' | 'cancel';
    plan?: PlanInfo;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const currentPlan = plans.find((p) => p.type === planType);
  const isLoading = subLoading || balanceLoading;
  const billingDisabled = personalWorkspaceEnabled === false;
  const isFree = planType === 'free';
  const showPaidTopUps = !billingDisabled && !isFree && usagePercent >= 80;
  const topUpDescription =
    usagePercent >= 100
      ? "You've used all your credits this month. Top-up to keep building."
      : "You're running low on credits. Top-up to keep building after your monthly credits are used up.";
  const pendingPlan = subscription?.pendingPlanType
    ? plans.find((p) => p.type === subscription.pendingPlanType)
    : null;
  const paidPlanCards = getDisplayPaidPlans(plans);


  const renewalLabel = (() => {
    if (!currentPlan || !periodEnd) return '';
    const endDate = new Date(periodEnd);
    const daysLeft = Math.max(
      0,
      Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
    );
    if (subscription?.cancelAtPeriodEnd) {
      if (daysLeft === 0) return 'Cancels today';
      return daysLeft <= 30
        ? `Cancels in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`
        : `Cancels ${endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
    }
    const nextCredits = pendingPlan
      ? `${pendingPlan.creditsPerMonth.toLocaleString()} (${pendingPlan.name})`
      : currentPlan.creditsPerMonth.toLocaleString();
    if (daysLeft === 0) return `Renews today , credits renew at ${nextCredits}`;
    return daysLeft <= 30
      ? `Renews in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} , credits renew at ${nextCredits}`
      : `Renews ${endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  })();

  const handleUpgrade = async (plan: PlanInfo) => {
    if (billingDisabled) {
      setError('Billing is managed by your agency workspace.');
      return;
    }

    if (!subscription || subscription.status === 'revoked') {
      try {
        setCheckoutLoading(true);
        saveCheckoutMeta({
          type: 'subscription',
          previousPlanType: planType,
          previousCreditsPerMonth: currentPlan?.creditsPerMonth ?? 1_000,
          targetPlanType: plan.type,
          targetCreditsPerMonth: plan.creditsPerMonth,
        });
        await authClient.checkout({ slug: plan.checkoutSlug });
      } catch {
        setCheckoutLoading(false);
        setError('Failed to start checkout. Please try again.');
      }
      return;
    }
    setConfirmAction({ type: 'change', plan });
  };

  const handleConfirmChange = async () => {
    if (!confirmAction?.plan) return;
    setActionLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/v2/cloud/subscription/change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ planType: confirmAction.plan.type }),
      });

      if (!res.ok) {
        throw new Error(await getErrorMessage(res, 'Failed to change plan'));
      }

      const data = await res.json();
      if (data.action === 'checkout' && data.checkoutSlug) {
        setCheckoutLoading(true);
        saveCheckoutMeta({
          type: 'subscription',
          previousPlanType: planType,
          previousCreditsPerMonth: currentPlan?.creditsPerMonth ?? 1_000,
          targetPlanType: confirmAction.plan.type,
          targetCreditsPerMonth: confirmAction.plan.creditsPerMonth,
        });
        await authClient.checkout({ slug: data.checkoutSlug });
      } else {
        // Both 'changed' (upgrade) and 'scheduled' (downgrade) need a refetch
        await refetchSubscription();
      }
    } catch (err) {
      setCheckoutLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to change plan');
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleCancel = () => {
    setConfirmAction({ type: 'cancel' });
  };

  const handleConfirmCancel = async () => {
    setActionLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/v2/cloud/subscription/cancel`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error(await getErrorMessage(res, 'Failed to cancel subscription'));
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to cancel subscription',
      );
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    setError('');

    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/subscription/reactivate`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );

      if (!res.ok) {
        throw new Error(await getErrorMessage(res, 'Failed to reactivate subscription'));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to reactivate subscription',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelDowngrade = async () => {
    setActionLoading(true);
    setError('');

    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/subscription/cancel-downgrade`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );

      if (!res.ok) {
        throw new Error(await getErrorMessage(res, 'Failed to cancel downgrade'));
      }
      await refetchSubscription();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to cancel downgrade',
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <SettingsLayout activeTab="plans">
      {checkoutLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
          <span className="relative flex h-[18px] w-[18px] items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/60" />
            <span className="relative inline-flex h-[15px] w-[15px] rounded-full bg-white/70" />
          </span>
        </div>
      )}

      <div className="space-y-8">
        <h1 className="text-2xl font-semibold text-white">Subscription</h1>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <PulsingDot />
          </div>
        ) : (
          <>
            {error && (
              <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            {billingDisabled && (
              <div className="rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3 text-sm text-white/60">
                Your access is managed through an agency workspace. Personal
                billing and personal site credits are not enabled for this
                account.
              </div>
            )}

            {/* Cancellation banner */}
            {subscription?.cancelAtPeriodEnd && (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-amber-300">
                  Your subscription will end on{' '}
                  {periodEnd
                    ? new Date(periodEnd).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'the end of the current period'}
                </span>
                <button
                  onClick={handleReactivate}
                  disabled={actionLoading}
                  className="text-sm font-medium text-amber-300 hover:text-amber-200 underline"
                >
                  Reactivate
                </button>
              </div>
            )}

            {/* Pending downgrade banner */}
            {pendingPlan && !subscription?.cancelAtPeriodEnd && (
              <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-blue-300">
                  Your plan will switch to {pendingPlan.name} on{' '}
                  {periodEnd
                    ? new Date(periodEnd).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'the next billing date'}
                </span>
                <button
                  onClick={handleCancelDowngrade}
                  disabled={actionLoading}
                  className="text-sm font-medium text-blue-300 hover:text-blue-200 underline"
                >
                  Keep current plan
                </button>
              </div>
            )}

            {/* Compact credit context bar */}
            <section>
              <GradientBorder innerClassName="px-5 py-4">
                <div className="flex items-center gap-4">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider shrink-0 ${getPlanBadgeStyle(planType)}`}
                  >
                    {isFree ? 'Free' : currentPlan?.name ?? planType}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${getBarColor(usagePercent)}`}
                        style={{ width: `${Math.min(usagePercent, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-white/50 tabular-nums shrink-0">
                    {monthlyRemaining.toLocaleString()} / {periodAllowance.toLocaleString()} remaining
                  </span>
                  <a
                    href="/settings/usage"
                    className="text-xs text-white/40 hover:text-white/60 transition-colors shrink-0"
                  >
                    View details &rarr;
                  </a>
                </div>
                {!isFree && renewalLabel && (
                  <p className="text-xs text-white/40 mt-2">{renewalLabel}</p>
                )}
              </GradientBorder>
            </section>

            {/* Top-ups section (paid users: before plans) */}
            {showPaidTopUps && topUps.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-white mb-1">
                  Top-ups
                </h2>
                <p className="text-sm text-white/50 mb-6">
                  {topUpCredits > 0
                    ? 'Want even more room to build? Top-up credits anytime.'
                    : topUpDescription}
                </p>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {topUps.map((topUp, index) => {
                    const isRecommended = index === 1;
                    return (
                      <div
                        key={topUp.slug}
                        className={`relative flex flex-col items-center rounded-lg border px-5 py-6 gap-4 ${
                          isRecommended
                            ? 'border-white/30 bg-white/[0.05]'
                            : 'border-white/10 bg-white/[0.02]'
                        }`}
                      >
                        {isRecommended && (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-wider bg-white text-neutral-900 px-2 py-0.5 rounded-full">
                            Recommended
                          </span>
                        )}
                        <div className="text-center">
                          <span className="text-2xl font-bold text-white tabular-nums">
                            {topUp.credits.toLocaleString()}
                          </span>
                          <span className="text-xs text-white/50 ml-1">
                            credits
                          </span>
                        </div>
                        <Button
                          variant={isRecommended ? 'default' : 'outline'}
                          size="sm"
                          className="w-full"
                          disabled={topUpLoading !== null}
                          onClick={async () => {
                            setTopUpLoading(topUp.slug);
                            try {
                              saveCheckoutMeta({
                                type: 'topup',
                                credits: topUp.credits,
                                previousCredits: topUpCredits,
                              });
                              await authClient.checkout({
                                slug: topUp.slug,
                              });
                            } catch (err) {
                              console.error('Checkout failed:', err);
                              setTopUpLoading(null);
                              setError(
                                'Failed to start checkout. Please try again.',
                              );
                            }
                          }}
                        >
                          {topUpLoading === topUp.slug ? (
                            <span className="relative flex h-3 w-3">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
                              <span className="relative inline-flex h-3 w-3 rounded-full bg-current" />
                            </span>
                          ) : (
                            `Top-up for $${topUp.price}`
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-white/50 text-center mt-4">
                  Top-up credits never expire.
                </p>
              </section>
            )}

            {/* Plan comparison */}
            <section>
              <h2 className="text-lg font-semibold text-white mb-1">
                {isFree ? 'Choose your plan' : 'Plans'}
              </h2>
              <div className="mb-6 mt-2 border-y border-white/10 py-3">
                <PlanIntro />
              </div>

              <div className="space-y-5">
                <PlanComparison
                  plans={paidPlanCards}
                  currentPlanType={planType}
                  pendingPlanType={subscription?.pendingPlanType}
                  disabled={actionLoading || billingDisabled}
                  getActionLabel={(plan) =>
                      currentPlan && plan.price < currentPlan.price
                        ? `Switch to ${plan.name}`
                        : plan.marketing.cta
                  }
                  onSelect={handleUpgrade}
                />

                <div>
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/35">
                    What credits get you
                  </p>
                  <CreditUsageGrid />
                </div>
              </div>

              <p className="text-xs text-white/50 text-center mt-4">
                {!isFree && !subscription?.cancelAtPeriodEnd ? (
                  <>
                    You can{' '}
                    <button
                      onClick={handleCancel}
                      className="text-white/40 hover:text-white/60 transition-colors underline"
                    >
                      cancel your plan
                    </button>{' '}
                    anytime.
                  </>
                ) : (
                  'Cancel anytime.'
                )}
              </p>
            </section>

            {/* Top-ups section (free users: after plans) */}
            {!billingDisabled && isFree && topUps.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-white mb-1">
                  Plan top-up credits
                </h2>
                <p className="text-sm text-white/50 mb-6">
                  Upgrade to a paid plan to buy additional credits anytime you need them.
                </p>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {topUps.map((topUp) => (
                    <div
                      key={topUp.slug}
                      className="flex flex-col items-center rounded-lg border border-white/10 bg-white/[0.02] px-5 py-5 gap-1"
                    >
                      <span className="text-2xl font-bold text-white tabular-nums">
                        {topUp.credits.toLocaleString()}
                      </span>
                      <span className="text-xs text-white/50">
                        credits for ${topUp.price}
                      </span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-white/50 text-center mt-4">
                  Top-ups available to paid plan subscribers. Top-up credits never expire.
                </p>
              </section>
            )}
          </>
        )}
      </div>

      {/* Change plan confirmation */}
      <AlertDialog
        open={confirmAction?.type === 'change'}
        onOpenChange={() => setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.plan &&
              currentPlan &&
              confirmAction.plan.price > currentPlan.price
                ? `Upgrade to ${confirmAction.plan.name}`
                : `Switch to ${confirmAction?.plan?.name ?? ''}`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                {confirmAction?.plan &&
                currentPlan &&
                confirmAction.plan.price > currentPlan.price ? (
                  <>
                    <p>
                      Your plan will change from {currentPlan.name} ($
                      {currentPlan.price}/mo) to {confirmAction.plan.name} ($
                      {confirmAction.plan.price}/mo).
                    </p>
                    <p>
                      You'll be charged a prorated amount today for the
                      remainder of this billing period. Starting next month,
                      you'll be billed ${confirmAction.plan.price}/month.
                    </p>
                  </>
                ) : confirmAction?.plan &&
                  currentPlan &&
                  confirmAction.plan.price < currentPlan.price ? (
                  <>
                    <p>
                      Your plan will switch to {confirmAction.plan.name} ($
                      {confirmAction.plan.price}/mo) at the end of your current
                      billing period
                      {periodEnd
                        ? ` on ${new Date(periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
                        : ''}
                      .
                    </p>
                    <p>
                      You'll keep your {currentPlan.name} credits until then.
                    </p>
                  </>
                ) : (
                  <p>
                    You're switching to the {confirmAction?.plan?.name} plan ($
                    {confirmAction?.plan?.price}/month).
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmChange} disabled={actionLoading}>
              {actionLoading
                ? 'Changing...'
                : confirmAction?.plan &&
                    currentPlan &&
                    confirmAction.plan.price > currentPlan.price
                  ? `Upgrade for $${confirmAction.plan.price}/mo`
                  : 'Schedule Change'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel subscription confirmation */}
      <AlertDialog
        open={confirmAction?.type === 'cancel'}
        onOpenChange={() => setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Your subscription will remain active until the end of the current
              billing period. You can reactivate anytime before then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={() => setConfirmAction(null)}>
              Keep Subscription
            </Button>
            <Button
              variant="danger"
              disabled={actionLoading}
              onClick={handleConfirmCancel}
            >
              {actionLoading ? 'Canceling...' : 'Cancel Subscription'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsLayout>
  );
}
